import React from "react"

const cn = (...a) => a.flat().filter(Boolean).join(" ")

const THEME_VARS = {
  "--app-base": "206 62 0",
  "--app-base-strong": "181 54 0",
  "--app-base-soft": "255 128 0",
  "--app-content": "27 27 34",
  "--app-content-subtle": "111 107 118",
  "--app-content-faint": "139 133 146",
  "--brand-main": "255 76 0",
  "--brand-soft": "255 128 0",
  "--brand-strong": "206 62 0",
  "--brand-secondary": "111 107 118",
  "--bg-main": "247 242 234",
  "--bg-secondary": "247 242 234",
  "--bg-muted": "206 62 0",
  "--bg-accent": "255 76 0",
  "--surface-main": "255 255 255",
  "--surface-soft": "244 237 227",
  "--text-main": "27 27 34",
  "--text-secondary": "111 107 118",
  "--text-muted": "111 107 118",
  "--text-inverse": "255 255 255",
  "--success": "206 62 0",
  "--second-success": "206 62 0",
  "--accent-main": "206 62 0",
  "--radius-md": "12px",
  "--radius-lg": "20px",
  "--radius-xl": "20px",
  "--radius-button": "999px",
  "--radius-input": "12px",
  "--radius-card": "20px",
}

const base = String.raw`
  w-full font-[Nunito,system-ui,sans-serif]
  [&>div]:!max-w-full
  [&_.min-h-screen]:!min-h-0
  [&_.bg-second-white]:!bg-transparent [&_.bg-second-white]:!p-0 [&_.bg-second-white]:!min-h-0
  [&_.bg-bg-secondary]:!bg-transparent [&_.bg-bg-secondary]:!p-0 [&_.bg-bg-secondary]:!min-h-0

  [&_.relative.bg-white.shadow-md]:!bg-white
  [&_.relative.bg-white.shadow-md]:!border [&_.relative.bg-white.shadow-md]:!border-[rgba(20,18,28,0.12)]
  [&_.relative.bg-white.shadow-md]:!rounded-xl
  [&_.relative.bg-white.shadow-md]:!shadow-none
  [&_.relative.bg-white.shadow-md]:!min-h-[52px]
  [&_.relative.bg-white.shadow-md]:!px-3
  [&_.relative.bg-white.shadow-md_svg]:!text-[#ce3e00]

  [&_.self-start.bg-white.rounded-md]:!bg-[#f4ede3]
  [&_.self-start.bg-white.rounded-md]:!rounded-full
  [&_.self-start.bg-white.rounded-md]:!p-1
  [&_.self-start.bg-white.rounded-md_button]:!rounded-full
  [&_.self-start.bg-white.rounded-md_button]:font-extrabold
  [&_.self-start.bg-white.rounded-md_button]:text-sm

  [&_button.bg-second-success]:!bg-[#ce3e00] [&_button.bg-second-success]:!text-white
  [&_button.bg-second-success]:!border-0 [&_button.bg-second-success]:!rounded-full
  [&_button.bg-second-success]:shadow-none
  [&_button.bg-second-success]:font-[Nunito,system-ui,sans-serif]
  [&_button.bg-second-success]:text-base [&_button.bg-second-success]:font-extrabold
  [&_button.bg-second-success]:min-w-[160px]
  [&_button.bg-second-success]:!px-7
  [&_button.bg-second-success:hover:not(:disabled)]:!bg-[#b53600]
  [&_button.bg-second-success:hover:not(:disabled)]:!text-white

  [&_.rounded-card]:!rounded-[20px] [&_.rounded-card]:!bg-[unset]
  [&_.bg-surface-main]:!rounded-[20px] [&_.bg-surface-main]:!bg-[unset]
  [&_[class*='450px']_.relative[class*='53px'].bg-white]:!bg-[unset]
`

// Не публичная версия: вкладки и селектор пользователя видны, стык вкладок и формы
const privateView = String.raw`
  [&_.bg-text-secondary\/10.w-full]:!rounded-[20px]
  [&_.bg-text-secondary\/10.w-full]:!rounded-tl-none
  [&_.w-fit.rounded-t-lg]:!rounded-t-[20px]
  [&_.w-fit.rounded-t-lg]:!rounded-b-none
  max-[1099px]:[&_.w-fit.rounded-t-lg]:!mt-10
  max-[1099px]:[&_.w-fit.rounded-t-lg]:!max-w-full
  max-[1099px]:[&_.w-fit.rounded-t-lg]:overflow-x-auto
`

// Публичная версия: вкладки и селектор пользователя скрыты, форма — белая карточка с тенью
const publicView = String.raw`
  [&_.w-fit.rounded-t-lg]:hidden
  [&_.w-fit.rounded-t-lg~*:not(.bg-text-secondary\/10)]:hidden

  [&_.bg-text-secondary\/10.w-full]:!bg-white
  [&_.bg-text-secondary\/10.w-full]:!pt-6
  [&_.bg-text-secondary\/10.w-full]:!px-6
  [&_.bg-text-secondary\/10.w-full]:!pb-[45px]
  [&_.bg-text-secondary\/10.w-full]:!gap-4
  [&_.bg-text-secondary\/10.w-full]:!rounded-[20px]
  [&_.bg-text-secondary\/10.w-full]:!shadow-[0_0_15px_rgba(0,0,0,0.08),0_24px_40px_rgba(0,0,0,0.04)]
  [&_.bg-text-secondary\/10.w-full]:!min-h-0
  [&_.bg-text-secondary\/10.w-full>div.flex]:!gap-3
  [&_.bg-text-secondary\/10.w-full>div.flex]:!items-center

  max-[1099px]:[&_.bg-text-secondary\/10.w-full]:!p-4
  max-[1099px]:[&_.bg-text-secondary\/10.w-full]:!rounded-2xl
`

// ≥1100px
const desktop = String.raw`
  min-[1100px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:flex-row
  min-[1100px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:flex-nowrap
  min-[1100px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:items-center
  min-[1100px]:[&_button.bg-second-success]:!w-auto
  min-[1100px]:[&_button.bg-second-success]:grow-0
  min-[1100px]:[&_button.bg-second-success]:shrink-0
  min-[1100px]:[&_button.bg-second-success]:basis-auto
`

// ≤1099px
const tablet = String.raw`
  max-[1099px]:max-w-full max-[1099px]:min-w-0 max-[1099px]:box-border
  max-[1099px]:[&>div]:max-w-full max-[1099px]:[&>div]:min-w-0 max-[1099px]:[&>div]:box-border

  max-[1099px]:[&_.bg-text-secondary\/10.w-full]:overflow-visible
  max-[1099px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:flex-col
  max-[1099px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:flex-nowrap
  max-[1099px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:!items-stretch
  max-[1099px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:w-full
  max-[1099px]:[&_.bg-text-secondary\/10.w-full>.flex.flex-col]:!gap-3

  max-[1099px]:[&_[class*='450px']]:!min-w-0
  max-[1099px]:[&_[class*='450px']]:!w-full
  max-[1099px]:[&_[class*='450px']]:!h-auto
  max-[1099px]:[&_[class*='450px']]:!min-h-0
  max-[1099px]:[&_[class*='450px']]:flex-col
  max-[1099px]:[&_[class*='450px']]:items-stretch
  max-[1099px]:[&_[class*='450px']]:!bg-transparent
  max-[1099px]:[&_[class*='450px']]:!border-0
  max-[1099px]:[&_[class*='450px']]:!shadow-none
  max-[1099px]:[&_[class*='450px']]:!p-0
  max-[1099px]:[&_[class*='450px']>.flex.items-center]:flex-col
  max-[1099px]:[&_[class*='450px']>.flex.items-center]:flex-nowrap
  max-[1099px]:[&_[class*='450px']>.flex.items-center]:items-stretch
  max-[1099px]:[&_[class*='450px']>.flex.items-center]:!gap-3
  max-[1099px]:[&_[class*='450px']>.flex.items-center]:w-full

  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:w-full
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:grow
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:shrink
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:basis-full
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:!h-[52px]
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:!min-h-[52px]
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:border
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:border-[rgba(20,18,28,0.12)]
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:rounded-xl
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:!bg-white
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:px-3
  max-[1099px]:[&_[class*='450px']_.relative[class*='53px']]:box-border

  max-[1099px]:[&_[class*='450px']_.shrink-0]:flex
  max-[1099px]:[&_[class*='450px']_.shrink-0]:items-center
  max-[1099px]:[&_[class*='450px']_.shrink-0]:justify-center
  max-[1099px]:[&_[class*='450px']_.shrink-0]:self-center
  max-[1099px]:[&_[class*='450px']_.shrink-0_button]:rotate-90
  max-[1099px]:[&_[class*='450px']_.shrink-0_button]:border-[#ce3e00]
  max-[1099px]:[&_[class*='450px']_.shrink-0_button]:rounded-xl
  max-[1099px]:[&_[class*='450px']_.shrink-0_button]:text-[#ce3e00]

  max-[1099px]:[&_.flex.items-center.w-full.gap-5]:flex-wrap
  max-[1099px]:[&_.flex.items-center.w-full.gap-5]:items-start
  max-[1099px]:[&_.flex.items-center.w-full.gap-5]:!gap-x-2
  max-[1099px]:[&_.flex.items-center.w-full.gap-5]:!gap-y-10
  max-[1099px]:[&_.flex.items-center.w-full.gap-5]:w-full
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:grow
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:shrink
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:basis-[calc(50%-4px)]
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:min-w-0
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:w-auto
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:box-border
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1:last-child]:basis-full
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1>.relative.bg-white]:!h-[52px]
  max-[1099px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1>.relative.bg-white]:!min-h-[52px]

  max-[1099px]:[&_button.bg-second-success]:w-full
  max-[1099px]:[&_button.bg-second-success]:min-w-0
  max-[1099px]:[&_button.bg-second-success]:box-border

  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!relative
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!top-auto
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!right-auto
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!left-auto
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!w-full
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!min-w-0
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!max-w-full
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:mt-2
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:!shadow-none
  max-[1099px]:[&_.absolute.right-0[class*='60px'][class*='280px']]:box-border
`

// ≤767px
const mobile = String.raw`
  max-[767px]:[&_.fixed.bottom-0[class*='425px']]:!h-auto
  max-[767px]:[&_.fixed.bottom-0[class*='425px']]:max-h-[85vh]
  max-[767px]:[&_.fixed.bottom-0[class*='425px']]:z-[80]
  max-[767px]:[&_.flex.gap-8]:!gap-0
  max-[767px]:[&_.flex.gap-8>.h-6.w-px]:hidden
  max-[767px]:[&_.flex.gap-8>.flex-1:last-child]:hidden
  max-[767px]:[&_.px-6.py-2.bg-white]:!px-3
`

// ≤479px
const small = String.raw`
  max-[479px]:[&_.flex.items-center.w-full.gap-5>.w-full.flex-1]:basis-full
`
const AviaSearchWrapper = ({ children, isPublic = true, className = "" }) => {
  return (
    <div
      id="avia-search-react"
      style={THEME_VARS}
      className={cn(
        base,
        isPublic ? publicView : privateView,
        desktop,
        tablet,
        mobile,
        small,
        className
      )}
    >
      {children}
    </div>
  )
}

export default AviaSearchWrapper
